import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  RiArrowDownSLine,
  RiDownload2Line,
  RiSearchLine,
  RiAddLine,
  RiDeleteBinLine,
  RiGroupLine,
  RiUserAddLine,
  RiMore2Line,
  RiEyeLine,
} from "@remixicon/react";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { CustomerCreateModal } from "./CustomerCreateModal";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/dashboard/shared/EmptyState";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from '@/utils/currency';

interface CustomerFilters {
  search: string;
  tags: string[];
  orderCount: {
    min?: number;
    max?: number;
  };
  totalSpent: {
    min?: number;
    max?: number;
  };
  dateJoined: {
    from?: Date;
    to?: Date;
  };
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export function CustomerList() {
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [filters, setFilters] = useState<CustomerFilters>({
    search: "",
    tags: [],
    orderCount: {},
    totalSpent: {},
    dateJoined: {},
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const { currentStore, storeId } = useCurrentStore();
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["customers", page, search, storeId],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search,
      });
      const response = await api.get(`/api/customers?${params}`, {
        headers: { "Store-ID": storeId },
      });
      return response.data;
    },
    enabled: !!storeId,
  });

  const bulkMutation = useMutation({
    mutationFn: async ({
      action,
      customerIds,
    }: {
      action: string;
      customerIds: string[];
    }) => {
      const { data } = await api.post(
        "/api/customers/bulk",
        { action, customerIds },
        {
          headers: { "Store-ID": storeId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["customers"]);
      setSelectedCustomers([]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (customerId: string) => {
      await api.delete(`/api/customers/${customerId}`, {
        headers: { "Store-ID": storeId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer deleted successfully");
      setCustomerToDelete(null);
      setShowDeleteDialog(false);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to delete customer"
      );
    },
  });

  const handleBulkAction = (action: string) => {
    if (selectedCustomers.length === 0) {
      toast.error("Please select customers first");
      return;
    }
    bulkMutation.mutate({ action, customerIds: selectedCustomers });
  };

  const handleDeleteCustomer = (customerId: string) => {
    setCustomerToDelete(customerId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (customerToDelete) {
      deleteMutation.mutate(customerToDelete);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post("/api/customers/import", formData, {
        headers: { "Content-Type": "multipart/form-data", "Store-ID": storeId },
      });
      queryClient.invalidateQueries(["customers"]);
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await api.get("/api/customers/export", {
        headers: { "Store-ID": storeId },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "customers-export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const getCurrencySymbol = () => {
    return currentStore?.currency || "$";
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SectionCard>
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <Skeleton className="h-9 flex-1 rounded-lg" />
              <div className="flex gap-2">
                <Skeleton className="h-9 w-32 rounded-lg" />
                <Skeleton className="h-9 w-28 rounded-lg" />
                <Skeleton className="h-9 w-24 rounded-lg" />
              </div>
            </div>
            {isMobile ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className={dashboardStyles.listItem}>
                    <div className="flex items-center gap-3 w-full">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <Skeleton className="h-4 w-36" />
                          <Skeleton className="h-7 w-7 rounded-md" />
                        </div>
                        <Skeleton className="h-3 w-48 mb-2.5" />
                        <div className="flex gap-2">
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={dashboardStyles.tableWrap}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Total Spent</TableHead>
                      <TableHead>Last Order</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-4 rounded" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-44" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-7 w-16 rounded-md" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionCard>
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="relative flex-1 min-w-0">
              <RiSearchLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                className={dashboardStyles.filterInput}
              />
            </div>

            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <Button
                onClick={() => setShowCreateModal(true)}
                className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
              >
                <RiAddLine className="h-4 w-4" />
                Add Customer
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
                  >
                    Actions{" "}
                    <RiArrowDownSLine className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {/*
                  <DropdownMenuItem onClick={() => handleBulkAction('tag')}>
                    Add Tag
                  </DropdownMenuItem>
                  */}
                  <DropdownMenuItem onClick={() => handleBulkAction("export")}>
                    Export Selected
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkAction("delete")}>
                    Delete Selected
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                onClick={handleExportAll}
                variant="outline"
                className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
              >
                <RiDownload2Line className="h-4 w-4" />
                Export
              </Button>
              {/*
              <label>
                <Button variant="outline" asChild className="gap-1 !py-2 !px-4 h-auto text-sm rounded-lg">
                  <div>
                    <Upload className="h-4 w-4" strokeWidth={2.5}/>
                    Import
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleImport}
                    />
                  </div>
                </Button>
              </label>
              */}
            </div>
          </div>

          {/* Empty State Handling */}
          {!isLoading && (!data?.customers || data.customers.length === 0) && (
            <EmptyState
              compact
              icon={<RiGroupLine className="h-4 w-4 text-muted-foreground" />}
                title={
                  filters.search ? "No customers found" : "No customers yet"
                }
                description={
                  filters.search
                    ? `No customers match your search for "${filters.search}". Try adjusting your search terms.`
                    : "Start building your customer base by adding your first customer."
                }
                action={
                  <div className="flex items-center gap-3 justify-center">
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
                    >
                      <RiUserAddLine className="h-4 w-4" />
                      Add Your First Customer
                    </Button>
                  </div>
                }
              />
          )}

          {/* Customer Table - Only show when there are customers */}
          {!isLoading && data?.customers && data.customers.length > 0 && (
            <>
              {/* Mobile Card View */}
              {isMobile ? (
                <div className="space-y-3">
                  {data?.customers.map((customer) => (
                    <CustomerCard
                      key={customer._id}
                      customer={customer}
                      onView={(customerId) =>
                        navigate(`/dashboard/customers/${customerId}`)
                      }
                      onDelete={handleDeleteCustomer}
                      getCurrencySymbol={getCurrencySymbol}
                    />
                  ))}
                </div>
              ) : (
                /* Desktop Table View */
                <div className={dashboardStyles.tableWrap}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">
                          <Checkbox
                            checked={
                              selectedCustomers.length ===
                              data?.customers.length
                            }
                            onCheckedChange={(checked) => {
                              setSelectedCustomers(
                                checked ? data?.customers.map((c) => c._id) : []
                              );
                            }}
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Orders</TableHead>
                        <TableHead>Total Spent</TableHead>
                        <TableHead>Last Order</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.customers.map((customer) => (
                        <TableRow key={customer._id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedCustomers.includes(customer._id)}
                              onCheckedChange={(checked) => {
                                setSelectedCustomers((ids) =>
                                  checked
                                    ? [...ids, customer._id]
                                    : ids.filter((id) => id !== customer._id)
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell>{customer.email}</TableCell>
                          <TableCell>{customer.phone || "-"}</TableCell>
                          <TableCell>{customer.orderCount}</TableCell>
                          <TableCell>
                            {getCurrencySymbol()}
                            {(customer.totalSpent || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {customer.lastOrder
                              ? format(
                                  new Date(customer.lastOrder),
                                  "MMM d, yyyy"
                                )
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/dashboard/customers/${customer._id}`)}
                                className="h-8 w-8 p-0"
                              >
                                <RiEyeLine className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDeleteCustomer(customer._id)
                                }
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              >
                                <RiDeleteBinLine className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}

          {/* Pagination - Only show when there are customers and multiple pages */}
          {!isLoading &&
            data?.customers &&
            data.customers.length > 0 &&
            data?.total > limit && (
              <div className={dashboardStyles.paginationBar + " justify-center"}>
                <Button
                  variant="outline"
                  className={dashboardStyles.paginationBtn}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {Math.ceil(data.total / limit)}
                </span>
                <Button
                  variant="outline"
                  className={dashboardStyles.paginationBtn}
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(data.total / limit)}
                >
                  Next
                </Button>
              </div>
            )}

          {/* Modals and Dialogs */}
          <CustomerCreateModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
          />

          <DeleteCustomerModal
            isOpen={showDeleteDialog}
            onCancel={() => setShowDeleteDialog(false)}
            onConfirm={confirmDelete}
          />
      </SectionCard>
    </div>
  );
}

// Customer Card Component for Mobile View
const CustomerCard = ({
  customer,
  onView,
  onDelete,
  getCurrencySymbol,
}: {
  customer: any;
  onView: (customerId: string) => void;
  onDelete: (customerId: string) => void;
  getCurrencySymbol: () => string;
}) => {
  return (
    <div className={dashboardStyles.listItem}>
      <div className="flex items-start gap-3 w-full">
          {/* Customer Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate text-sm">
                  {customer.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">
                  {customer.email}
                </p>
              </div>

              {/* Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <RiMore2Line className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onView(customer._id)}>
                    <RiEyeLine className="w-4 h-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(customer._id)}
                    className="text-red-600"
                  >
                    <RiDeleteBinLine className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Customer Stats */}
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-xs">
                {customer.orderCount} orders
              </Badge>
              <Badge variant="outline" className="text-xs">
                {formatPrice(customer.totalSpent || 0, getCurrencySymbol())}
              </Badge>
            </div>

            {/* Additional Info */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{customer.phone || "No phone"}</span>
              <span>
                {customer.lastOrder
                  ? format(new Date(customer.lastOrder), "MMM d, yyyy")
                  : "No orders"}
              </span>
            </div>
          </div>
        </div>
    </div>
  );
};

const DeleteCustomerModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 font-heading">
            Delete Customer
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Are you sure you want to delete this customer? This action cannot be
            undone. Customers with existing orders cannot be deleted.
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            size="sm"
            onClick={onConfirm}
          >
            Delete Customer
          </Button>
        </div>
      </div>
    </div>
  );
};
